import dh from "../../dataHelpers/dataHelper";
import ENICountries from "@/assets/config/ENICountries.json";

import * as d3Scale from "d3-scale";
import { orderBy as _orderBy, maxBy as _maxBy } from "lodash";

import timeSeriesData from "@/assets/data/timeSeries.json";
import totalShareData from "@/assets/data/shareOnTotalWorld.json";
import top5Data from "@/assets/data/Top5.json";
import countries from "@/assets/data/countries.json";
import oilQualityTrend from "@/assets/data/OilQualityTrend.json";
import oilQualityPreviousYear from "@/assets/data/OilQualityPreviousYear.json";
import oilQualityPercentage from "@/assets/data/OilQualityPercentage.json";
import oilQualityConfig from "@/assets/data/OilQualityConfig.json";

const state = {
  // loaded datasets
  dataSets: {
    timeSerie: null,
    totalShare: null,
    top5: null,
    oilQualityTrend: null,
    oilQualityPreviousYear: null,
    oilQualityPercentage: null,
  },
  // properties ready for components
  timeSerie: null,
  totalShare: null,
  top5: null,
  oilQualityTrend: null,
  oilQualityPreviousYear: null,
  oilQualityPercentage: null,
  lastUpdated: null,
  growth: null,
  ENICountries: ENICountries,
  isNoData: false,
};

const mutations = {
  setDataset(state, payload) {
    state.dataSets[payload.dataset] = payload.data;
  },
  setTimeSerie(state, { data }) {
    state.timeSerie = data;
  },
  setTotalShare(state, { data }) {
    state.totalShare = data;
  },
  setTop5(state, { data }) {
    state.top5 = data;
  },
  setOilQualityTrend(state, { data }) {
    state.oilQualityTrend = data;
  },
  setOilQualityPreviousYear(state, { data }) {
    state.oilQualityPreviousYear = data;
  },
  setOilQualityPercentage(state, { data }) {
    state.oilQualityPercentage = data;
  },
  setLastUpdated(state, data) {
    state.lastUpdated = data;
  },
  setGrowth(state, { data }) {
    state.growth = data;
  },
  setIsNoData(state, data) {
    state.isNoData = data;
  },
};

const getters = {
  getDataset: (state) => (field) => {
    return state.dataSets[field];
  },
  getTimeSerie: (state) => {
    return state.timeSerie;
  },
  getTotalShare: (state) => {
    return state.totalShare;
  },
  getTop5: (state) => {
    return state.top5;
  },
  getOilQualityTrend: (state) => state.oilQualityTrend,
  getOilQualityPercentage: (state) => state.oilQualityPercentage,
  getOilQualityPreviousYear: (state) => state.oilQualityPreviousYear,
  getLastUpdated: (state) => {
    return state.lastUpdated;
  },
  getGrowth: (state) => {
    return state.growth;
  },
  getEniCountries: (state) => {
    return state.ENICountries;
  },
  isNoData: (state) => {
    return state.isNoData;
  },
};

// const dataAction = { root: true };
const actions = {
  loadData({ dispatch }) {
    // import and parse datasets
    dispatch("loadTimeSerie");
    dispatch("loadTotalShare");
    dispatch("loadTop5");
    dispatch("loadOilQualityTrend");
    dispatch("loadOilQualityPercentage");
    dispatch("loadOilQualityPreviousYear");
  },
  loadTimeSerie({ commit }) {
    const parsedData = dh.parseNumericValues(timeSeriesData, "QTY");

    commit("setDataset", { data: parsedData, dataset: "timeSerie" });
  },
  loadTotalShare({ commit }) {
    let parsedData = dh.parseNumericValues(totalShareData, "QTY");
    parsedData = dh.parsePercValues(parsedData, "QTY%");

    commit("setDataset", { data: parsedData, dataset: "totalShare" });
  },
  loadTop5({ commit }) {
    let parsedData = dh.parseNumericValues(top5Data, "QTY");
    parsedData = dh.parseNumericValues(parsedData, "RANK");
    parsedData = dh.parsePercValues(parsedData, "QTY%");
    parsedData = dh.filterAwayZeroOrNull(parsedData, "QTY");

    commit("setDataset", { data: parsedData, dataset: "top5" });
  },
  loadOilQualityTrend({ commit }) {
    let parsedData = [];

    for (const year of oilQualityConfig.map((o) => o["YEARS"])) {
      parsedData = dh.parseNumericValues(oilQualityTrend, year);
      parsedData = dh.filterAwayZeroOrNull(parsedData, year);
    }

    commit("setDataset", { data: parsedData, dataset: "oilQualityTrend" });
  },
  loadOilQualityPercentage({ commit }) {
    let parsedData = [];

    parsedData = dh.parsePercValues(oilQualityPercentage, "PERC");
    parsedData = dh.filterAwayZeroOrNull(parsedData, "PERC");

    commit("setDataset", { data: parsedData, dataset: "oilQualityPercentage" });
  },
  loadOilQualityPreviousYear({ commit }) {
    let parsedData = [];
    for (const field of oilQualityConfig
      .map((o) => o.previuos_year_fields)
      .filter((o) => o !== "")) {
      parsedData = dh.parseNumericValues(oilQualityPreviousYear, field);
      parsedData = dh.filterAwayZeroOrNull(parsedData, field);
    }
    commit("setDataset", {
      data: parsedData,
      dataset: "oilQualityPreviousYear",
    });
  },
  calcData({ dispatch }, payload = {}) {
    // no need to update totalShare when only area is changed
    const excludeTotalShare = payload.excludeTotalShare || false;
    if (!excludeTotalShare) {
      dispatch("getTotalShare");
    }

    dispatch("getTimeSerie");
    dispatch("getTop5");
    dispatch("getGrowth");
    dispatch("getOilQualityTrend");
    dispatch("getOilQualityPercentage");
    dispatch("getOilQualityPreviousYear");
  },
  getTimeSerie({ commit, rootGetters, getters }) {
    const filters = dh.getFiltersState(rootGetters);
    let dataset = getters.getDataset("timeSerie");

    dataset = dh.filterByMainSource(dataset, filters.mainSource);
    dataset = dh.filterByGeoArea(dataset, filters.geoArea);
    dataset = dh.filterBySource(dataset, filters.source);
    dataset = dh.filterByDataType(dataset, filters.dataType);

    if (filters.geoArea === "Americas") {
      // when Americas sum QTY by year because in data we have "North Americas" and "Central South Americas" in the data but just "Americas" in the chips
      dataset = dh.sumByPropery(dataset, "QTY", "YEAR");
    }

    // get last updated year dynamically
    // check if dataset length isn't zero

    const lastUpdated =
      dataset.length !== 0 && _orderBy(dataset, ["YEAR"], ["desc"])[0].YEAR;

    //  console.log('timeSerie!', dataset);

    commit("setLastUpdated", lastUpdated);
    commit("setTimeSerie", { data: dataset });
  },
  getTotalShare({ commit, rootGetters, getters }) {
    const filters = dh.getFiltersState(rootGetters);
    let dataset = getters.getDataset("totalShare");

    dataset = dh.filterByMainSource(dataset, filters.mainSource);
    dataset = dh.filterBySource(dataset, filters.source);
    dataset = dh.filterByDataType(dataset, filters.dataType);
    dataset = _orderBy(dataset, ["QTY%"], ["desc"]);

    commit("setTotalShare", { data: dataset });
  },
  getTop5({ commit, rootGetters, getters }) {
    // TOP5 AND MAP SHARES DATA
    const filters = dh.getFiltersState(rootGetters);
    let dataset = getters.getDataset("top5");

    dataset = dh.filterByMainSource(dataset, filters.mainSource);
    dataset = dh.filterBySource(dataset, filters.source);
    dataset = dh.filterByDataType(dataset, filters.dataType);

    // SCALE CIRCLE RADIUS PARAMS
    // calc MAX_QTY here before filering geoArea make a more accurate MAX and shared by the views of same sources
    if (dataset.length > 0) {
      const MAX_QTY = _maxBy(dataset, "QTY").QTY;

      // console.log("MAX_QTY", MAX_QTY);

      // Add dynamic radius range related to viewport width
      let width = window.innerWidth * 0.03;
      const calcMaxSize = Math.min(Math.max(20, width), 60);

      const circleSizeRange = [0, calcMaxSize]; // MIN AND MAX CIRCLE SIZES
      const scale_QTY = d3Scale
        .scaleSqrt()
        .domain([0, MAX_QTY])
        .range(circleSizeRange);

      dataset = dh.filterByGeoArea(dataset, filters.geoArea);

      // filter by unique country
      dataset = [
        ...new Map(dataset.map((item) => [item["COUNTRY"], item])).values(),
      ];

      // get country coords
      dataset = dataset.map((x, index) => {
        //MAP components needs datapoint to have a numeric(?) id to assign style to each circle on hover
        // this is used alsp used to make top5 component to communicate with map component

        x.ID = index;

        const circleSize = scale_QTY(x.QTY);
        const GEOITEM = dh.getCountryGeoCoords(x, circleSize, countries);

        return {
          COUNTRY: x.COUNTRY,
          RANK: x.RANK,
          "QTY%": x["QTY%"],
          GEO: GEOITEM,
          ID: x.ID, // Same ID as COORDS so that MAP and Top5 components can communicate
        };
      });
    }

    // console.log("TOP5", dataset);
    dataset = _orderBy(dataset, ["RANK"], ["asc"]);

    if (dataset.length === 0) {
      commit("setIsNoData", true);
    } else {
      commit("setIsNoData", false);
    }

    commit("setTop5", { data: dataset });
  },
  getGrowth({ commit, rootGetters, getters }) {
    const filters = dh.getFiltersState(rootGetters);
    let dataset = getters.getDataset("timeSerie"); //[NB] this is based on timeSerie dataset

    dataset = dh.filterByMainSource(dataset, filters.mainSource);
    dataset = dh.filterByGeoArea(dataset, filters.geoArea);
    dataset = dh.filterBySource(dataset, filters.source);
    dataset = dh.filterByDataType(dataset, filters.dataType);

    if (filters.geoArea === "Americas") {
      // when Americas sum QTY by year because in data we have "North Americas" and "Central South Americas" in the data but just "Americas" in the chips
      dataset = dh.sumByPropery(dataset, "QTY", "YEAR");
    }

    // console.log("GROWTH", dataset);

    dataset = dataset.map((x) => {
      return {
        YEAR: parseInt(x.YEAR),
        QTY: x.QTY,
      };
    });
    let res = [];

    dataset = _orderBy(dataset, ["YEAR"], ["desc"]);
    const latest = dataset[0];

    const delta_1 = dh.calcQTYPercChangeByDeltaYear(dataset, latest, 1);
    if (delta_1) {
      res.push(delta_1);
    }

    const diff = dh.calcQTYPercChangeByDeltaYear(
      dataset,
      latest,
      dataset.length - 1
    );
    if (diff) res.push(diff);

    commit("setGrowth", { data: res });
  },
  getOilQualityTrend({ commit, rootGetters, getters }) {
    const filters = dh.getFiltersState(rootGetters);
    let filteredData = getters.getDataset("oilQualityTrend");

    filteredData = dh.filterByGeoArea(filteredData, filters.geoArea);

    function onlyUnique(value, index, self) {
      return self.indexOf(value) === index;
    }
    // CONFIG for tva charts
    let config = {};
    const series = filteredData.map((o) => o.TYPE).filter(onlyUnique);
    config.series = series.map((o) => {
      return {
        key: o,
        label: o,
      };
    });
    config.xAxis = "year";

    // DATASET for tva charts
    const years = oilQualityConfig.map((o) => o["YEARS"]);
    let dataset = years.map((year) => {
      let tmp = {};
      config.series.forEach((o) => {
        tmp[o.key] = +filteredData.find((l) => l["TYPE"] === o.key)[year];
      });

      return {
        ...tmp,
        ...{ year },
      };
    });

    commit("setOilQualityTrend", { data: { dataset, config } });
  },
  getOilQualityPercentage({ commit, rootGetters, getters }) {
    const filters = dh.getFiltersState(rootGetters);
    let dataset = getters.getDataset("oilQualityPercentage");

    dataset = dh.filterByGeoArea(dataset, filters.geoArea);
    dataset = dataset.sort((a, b) => b.PERC - a.PERC);
    commit("setOilQualityPercentage", { data: dataset });
  },
  getOilQualityPreviousYear({ commit, rootGetters, getters }) {
    const filters = dh.getFiltersState(rootGetters);
    let dataset = getters.getDataset("oilQualityPreviousYear");

    dataset = dh.filterByGeoArea(dataset, filters.geoArea);
    dataset = dataset.sort((a, b) => a.COUNTRY.localeCompare(b.COUNTRY));

    // Config for tva charts
    let config = {
      xAxis: "sulphur",
      series: [
        {
          key: "api",
          label: "API Degree",
        },
      ],
    };

    // Config for tva charts
    let scatterData = dataset.map((o) => {
      return {
        label: o.COUNTRY,
        size: +o.PRODUCTION,
        sulphur: +o.SULPHUR,
        api: +o.API,
      };
    });

    commit("setOilQualityPreviousYear", {
      data: { dataset, config, scatterData },
    });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
